import { apiFetch, normalizeCursor } from '../../lib/api';

export type DiagnosticTargetType = 'user' | 'device';
export type DiagnosticLockStatus = 'active' | 'cancelled' | 'expired' | 'all';

export interface DiagnosticLock {
  lock_id: string;
  diagnostic_scope_id: string;
  target_type: DiagnosticTargetType;
  target_id: string;
  status: DiagnosticLockStatus;
  reason?: string;
  case_id?: string;
  created_by?: string;
  created_by_role?: string;
  created_at: number;
  expires_at: number;
  updated_at: number;
  cancelled_at?: number;
  cancelled_by?: string;
  cancel_reason?: string;
  log_query: string;
}

/**
 * 客戶端衍生實際狀態:server 標 `active` 但已過 `expires_at`(µs)→ 視為 `expired`。
 * 診斷鎖定授權「範圍化 log 存取」,顯示凍結的「使用中」會高報當前授權範圍;以此在
 * 每次 render 依現在時間衍生,不必等手動刷新才降級。其餘狀態(cancelled/expired)照 server。
 */
export function effectiveLockStatus(lock: Pick<DiagnosticLock, 'status' | 'expires_at'>): DiagnosticLockStatus {
  if (lock.status === 'active' && lock.expires_at > 0 && lock.expires_at <= Date.now() * 1000) {
    return 'expired';
  }
  return lock.status;
}

export interface DiagnosticLockListParams {
  targetType?: DiagnosticTargetType | '';
  targetId?: string;
  status?: DiagnosticLockStatus | '';
  cursor?: string;
  limit?: number;
}

export interface DiagnosticLockListPage {
  locks: DiagnosticLock[];
  nextCursor: string | null;
}

export interface CreateDiagnosticLockInput {
  targetType: DiagnosticTargetType;
  targetId: string;
  ttlMinutes: number;
  reason: string;
  caseId: string;
}

export async function listDiagnosticLocks(params: DiagnosticLockListParams = {}): Promise<DiagnosticLockListPage> {
  const res = await apiFetch<{ locks?: DiagnosticLock[]; next_cursor?: string }>('/v3/admin/diagnostic-locks', {
    query: {
      target_type: params.targetType,
      target_id: params.targetId,
      status: params.status,
      cursor: params.cursor,
      limit: params.limit,
    },
  });
  return {
    locks: Array.isArray(res.data?.locks) ? res.data.locks : [],
    nextCursor: normalizeCursor(res.data?.next_cursor),
  };
}

export async function createDiagnosticLock(input: CreateDiagnosticLockInput): Promise<DiagnosticLock> {
  const res = await apiFetch<DiagnosticLock>('/v3/admin/diagnostic-locks', {
    method: 'POST',
    body: {
      target_type: input.targetType,
      target_id: input.targetId,
      ttl_minutes: input.ttlMinutes,
      reason: input.reason,
      case_id: input.caseId,
    },
  });
  return res.data;
}

export async function cancelDiagnosticLock(lockId: string, reason?: string, caseId?: string): Promise<DiagnosticLock> {
  const body: Record<string, string> = {};
  if (reason) body.reason = reason;
  if (caseId) body.case_id = caseId;
  const res = await apiFetch<DiagnosticLock>(`/v3/admin/diagnostic-locks/${encodeURIComponent(lockId)}`, {
    method: 'DELETE',
    ...(Object.keys(body).length > 0 ? { body } : {}),
  });
  return res.data;
}
